import React, { useEffect, useState, useRef, useMemo } from 'react';
import AnyChart from 'anychart-react/dist/anychart-react';
import anychart from 'anychart';
import styles from './ChartWithTable.module.scss';
import PropTypes from 'prop-types';

/** @param {CHART_SETTINGS} props */
const Table = ({ series, data, source }) => (
  <div className={styles.Container}>
    <div className={styles.Master}>
      <header>
        <div />
      </header>
      <div className={styles.rowsGroup}>
        {source
          .map((source) => (
            <div key={source.label} className={styles.rowGroup}>
              <div
                className={styles.rowCell}
                style={{ backgroundColor: toColor(source.color) }}
              >
                {source.label}
              </div>
            </div>
          ))
          .concat(
            <div key='lastrow' className={styles.rowGroup}>
              <div className={styles.rowCell}>&nbsp;</div>
            </div>,
          )}
      </div>
    </div>
    <div className={styles.Master}>
      <header>
        {series.map(({ label }, index) => (
          <div key={label + index.toString()} className={styles.headerCell}>
            {label}
          </div>
        ))}
      </header>
      <div className={styles.rowsGroup}>
        {source
          .map((source, sourceIndex) => (
            <div key={source.label} className={styles.rowGroup}>
              {series.map((_, serieIndex) => (
                <div
                  key={_.label + serieIndex.toString()}
                  className={styles.rowCell}
                >
                  {source.valueLabel(
                    source.value(data[serieIndex][sourceIndex]),
                  )}
                </div>
              ))}
            </div>
          ))
          .concat(
            <div key='lastrow' className={styles.rowGroup}>
              {series.map((_, index) => (
                <div
                  key={_.label + index.toString()}
                  className={styles.rowCell}
                >
                  &nbsp;
                </div>
              ))}
            </div>,
          )}
      </div>
    </div>
  </div>
);

/** @type {CHART_DATA} */
const EXAMPLE_VALUE = {
  data: [
    [{ value: 1 }, { value: 3 }, { aCustomProp: 6 }],
    [{ value: 2 }, { value: 3 }, { aCustomProp: 5 }],
    [{ value: 3 }, { value: 3 }, { aCustomProp: 4 }],
    [{ value: 4 }, { value: 3 }, { aCustomProp: 5 }],
    [{ value: 3 }, { value: 3 }, { aCustomProp: 6 }],
    [{ value: 2 }, { value: 3 }, { aCustomProp: 8 }],
    [{ value: 1 }, { value: 3 }, { aCustomProp: 9 }],
  ],
  series: [
    {
      label: 'Time 1',
    },
    {
      label: 'Time 2',
    },
    {
      label: 'Time 3',
    },
    {
      label: 'Time 4',
      chartLabel: 'Time 4 with a custom label',
    },
    {
      label: 'Time 5',
    },
    {
      label: 'Time 6',
    },
    {
      label: 'Time 7',
    },
  ],
  source: [
    {
      color: {
        red: 255,
        blue: 0,
        green: 80,
      },
      label: 'ONE MEASURE VALUE',
      mode: 'column_stack',
      value: (i) => i.value,
      valueLabel: (v) => `${v} kg/ha`,
    },
    {
      color: {
        red: 255,
        blue: 0,
        green: 1,
      },
      label: 'OTHER MEASURE VALUE',
      mode: 'column_stack',
      value: (i) => i.value,
      valueLabel: (v) => `${v} KG/HA`,
    },
    {
      color: {
        red: 255,
        blue: 255,
        green: 1,
      },
      label: 'A LINE MEASURE',
      mode: 'line',
      value: (i) => i.aCustomProp,
      valueLabel: (v) => `${v} HA`,
    },
  ],
  title: 'AN EXAMPLE TITLE',
};

/**
 * @param {RGB} color
 * @param {number} alpha
 */
const toColor = (color, alpha = 1) =>
  `rgba(${color.red}, ${color.green}, ${color.blue}, ${alpha})`;

/**
 * This components render a chart along with a table to display the values
 * @typedef {{red: number, green: number, blue: number}} RGB
 * @typedef {"column_stack" | "line" | undefined} Modes
 * //'stack' All data with this mode will be stacked for each series
 * //'line' The series will be displayed as a line
 * //'bar' The value will be displayed allongside with the ones set as stack
 * //undefined The value will not be displayed on the graphic but will be displayed on the table
 * You need to pass the following props
 * @typedef {{
 *  yScale: {
 *      label: (value: any) => any, //A label para cada um dos itens da escala
 *      title: string //O titulo do eixo Y
 *  },
 *  title: any, //The title of the chart
 *  series: {
 *      label: string, //The columns headers
 *      chartLabel: string, //The label for each series of the chart
 *  }[],
 *  source: {
 *      label: string, //The title of each data
 *      value: (item: T) => V, //The value of each item of the data
 *      valueLabel: (value?: V) => any, //This function will set the display name of the value
 *      mode?: Modes, //How the information should be displayed
 *      color: RGB //The color to be used
 *      chartColor: RGB //Custom color for the chart only, if undefined uses the color prop
 *  }[],
 *  data: T[][]
 * }} CHART_SETTINGS
 * @param {{settings: CHART_SETTINGS, showTable: boolean}} props
 */
export default function ChartWithTable(props) {
  const {
    settings: {
      yScale: { label: yLabel, title: yTitle } = {},
      data,
      series = [],
      source,
      title,
    } = EXAMPLE_VALUE,
    showTable = true,
    settings,
    chartContainerRef,
  } = props;
  const [instance] = useState(anychart.graphics.create());
  const [chart, setChart] = useState();
  const { current: optimizationMao } = useRef({
    areas: [],
    columnStacks: [],
  });
  useEffect(() => {
    if (!instance || series.length === 0) return;
    // Obtém uma referencia para a instancia de chart vazia
    const cartesian = !chart ? anychart.cartesian() : chart;
    let sourcesIndexes;
    cartesian.title(title);
    // Obtém os indices dos source que devem ter o formato de stack
    sourcesIndexes = source.reduce(
      (result, s, i) => (s.mode === 'line' ? result.concat(i) : result),
      [],
    );
    sourcesIndexes.forEach((sourceIndex, index) => {
      const currSource = source[sourceIndex];
      const seriesData = series.map((serie, serieIndex) => {
        const value = {
          x: serieIndex,
          label: serie.chartLabel || serie.label,
          value: currSource.value(data[serieIndex][sourceIndex]),
        };
        return value;
      });
      if (!optimizationMao.areas[index]) {
        const chartArea = cartesian.area(seriesData);
        chartArea.yScale(null);
        chartArea
          .tooltip()
          .format((e) => `${e.seriesName}: ${currSource.valueLabel(e.value)}`);
        optimizationMao.areas[index] = chartArea;
      } else {
        const dataset = optimizationMao.areas[index].data().getDataSets()[0];

        seriesData.forEach((r, i) => {
          dataset.row(i, r);
        });

        for (let i = seriesData.length; i < dataset.getRowsCount(); i++) {
          dataset.remove(i);
        }
      }
      const chartArea = optimizationMao.areas[index];
      chartArea.name(source[sourceIndex].label);
      [chartArea.hovered(), chartArea.selected(), chartArea.normal()].forEach(
        (state) => {
          state
            .fill(toColor(currSource.chartColor || currSource.color), 0.3)
            .stroke(toColor(currSource.chartColor || currSource.color))
            .hatchFill(
              anychart.graphics.hatchFill(
                'backward-diagonal',
                toColor(currSource.chartColor || currSource.color),
                1,
                15,
              ),
            );
        },
      );
    });

    sourcesIndexes = source.reduce(
      (result, s, i) => (s.mode === 'column_stack' ? result.concat(i) : result),
      [],
    );
    const stackTotalValue = [];
    if (sourcesIndexes.length > 0) {
      sourcesIndexes.forEach((sourceIndex, index) => {
        const currSource = source[sourceIndex];
        const seriesData = series.map((serie, serieIndex) => {
          const serieData = {
            x: serieIndex,
            label: serie.chartLabel || serie.label,
            value: source[sourceIndex].value(data[serieIndex][sourceIndex]),
            getTotal: (index) => stackTotalValue[index],
          };
          if (!stackTotalValue[serieIndex]) {
            stackTotalValue[serieIndex] = serieData.value;
          } else {
            stackTotalValue[serieIndex] += serieData.value;
          }
          return serieData;
        });
        if (!optimizationMao.columnStacks[index]) {
          const chartColumn = cartesian
            .column(seriesData)
            .name(currSource.label)
            .yScale(
              index > 0
                ? optimizationMao.columnStacks[0].yScale()
                : anychart.scales.linear().stackMode('value').minimum(0),
            );
          chartColumn
            .tooltip()
            .format(
              (e) => `${e.seriesName}: ${currSource.valueLabel(e.value)}`,
            );

          chartColumn
            .labels()
            .enabled(true)
            .fontColor('black')
            .fontSize(9)
            .position('center')
            .anchor('center')
            .format(function format() {
              const num = this.index;
              return (
                anychart.format.number(
                  (this.getData('value') / this.getData('getTotal')(num)) * 100,
                  0,
                  ',',
                  '.',
                ) + '%'
              );
            });
          optimizationMao.columnStacks[index] = chartColumn;
        } else {
          const chart = optimizationMao.columnStacks[index];
          const dataset = chart.data().getDataSets()[0];
          seriesData.forEach((r, i) => {
            dataset.row(i, r);
          });

          for (let i = seriesData.length; i < dataset.getRowsCount(); i++) {
            dataset.remove(i);
          }
        }
        const chartColumn = optimizationMao.columnStacks[index];
        [
          chartColumn.hovered(),
          chartColumn.selected(),
          chartColumn.normal(),
        ].forEach((state) => {
          state.stroke(toColor(currSource.chartColor || currSource.color));
          state.fill(toColor(currSource.chartColor || currSource.color));
        });
      });
    }
    const yAxis0 = cartesian.yAxis(0);
    yAxis0.enabled(false);
    cartesian.padding(0);
    const yAxis = cartesian.yAxis(1);
    yAxis.title(yTitle);
    yAxis.width(111);
    yAxis.ticks(null);
    yAxis.labels().format(({ value }) => yLabel(Number(value).toFixed(0)));
    cartesian
      .xAxis()
      .labels()
      .format(({ value }) => series[value].label);
    cartesian
      .tooltip()
      .displayMode('union')
      .positionMode('point')
      .position('center-top')
      .anchor('center-bottom')
      .offsetX(0)
      .offsetY(5)
      .titleFormat('{%label}');
    const minValue = cartesian.getStat('dataplotymin');
    const maxValue = cartesian.getStat('dataplotymax');
    const yScales = cartesian.getYScales();
    // set minimum and maximum y-scale
    for (let i = 0; i < yScales.length; i++) {
      const yScale = yScales[i];
      yScale.minimum(minValue > 0 ? minValue : 0);
      yScale.maximum(maxValue > 0 ? maxValue * 1.25 : 1);
    }
    setChart(cartesian);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, data, series, source, title, yTitle, yLabel]);
  const id = useMemo(() => (Date.now() + Math.random()).toFixed(0), []);
  useEffect(() => {
    chartContainerRef.current = instance;
  }, [instance, chartContainerRef]);
  return instance && chart ? (
    <div className={styles.container}>
      <AnyChart id={id} height={450} instance={instance} charts={[chart]} />
      {showTable && <Table {...(settings || EXAMPLE_VALUE)} />}
    </div>
  ) : null;
}

ChartWithTable.propTypes = {
  settings: PropTypes.shape({
    yScale: PropTypes.shape({
      label: PropTypes.func,
      title: PropTypes.string,
    }),
    // The title of the chart
    title: PropTypes.any,
    series: PropTypes.arrayOf(
      PropTypes.shape({
        // The columns headers
        label: PropTypes.string,
        // The label for each series of the chart, if undefined the label will be used
        chartLabel: PropTypes.string,
      }),
    ),
    source: PropTypes.arrayOf(
      PropTypes.shape({
        // The title of each data
        label: PropTypes.string,
        // The value of each item of the data
        value: () => PropTypes.object,
        // This function will set the display name of the value
        valueLabel: () => PropTypes.any,
        // How the information should be displayed
        mode: PropTypes.oneOf(['column_stack', 'line', undefined]),
        // The color to be used
        color: PropTypes.shape({
          red: PropTypes.number,
          green: PropTypes.number,
          blue: PropTypes.blue,
        }),
        // Custom color for the chart only
        chartColor: PropTypes.shape({
          red: PropTypes.number,
          green: PropTypes.number,
          blue: PropTypes.blue,
        }),
      }),
    ),
    data: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.any)),
  }).isRequired,
};

Table.propTypes = ChartWithTable.propTypes.settings;
